/**
 * useTokenGuard — validates the Alta access token on load and sends the browser
 * to the correct environment's sign-in page when it is not valid.
 *
 * Modelled on Platinum's `useVerifyAuthentication`
 * (`app/utils/customHooks/useVerifyAuthentication.tsx`): read the shared
 * `accessToken-<env>` cookie, ask Helium whether it is still valid, and on a
 * negative answer clear the cookie and hard-navigate to `<origin>/signin`.
 *
 * Intended to run once, above the router. See `src/App.jsx`.
 *
 * Deliberately inert unless the page is served from `*.alta.avigilon.com`.
 * Local development and the `*.gcp-dev.calipsa.biz` hosts authenticate with
 * this service's own dev-login session cookie (`src/context/AuthContext.jsx`),
 * have no Alta token to check, and have no sign-in page to be sent to.
 *
 * Fails open when Helium cannot be reached. An outage or a CORS rejection
 * returns UNKNOWN, and the app renders instead of bouncing every user to
 * sign-in; the API layer still rejects unauthorised calls with 401. Platinum
 * behaves the same way, because a failed query there leaves its effect without
 * data and so never redirects.
 */
import { useEffect, useRef, useState } from 'react';

import { getOpEnv, getSignInUrl } from '../utils/altaEnv';
import { getAccessToken, removeAccessToken } from '../utils/accessToken';
import {
  validateAccessToken,
  TOKEN_STATUS,
} from '../services/altaAuthService';

/**
 * @param {object} [options]
 * @param {boolean} [options.isRedirectUrlEnabled=true] Append `redirectUrl` so
 *   Alta returns the user to the page they asked for. Matches the option of the
 *   same name on Platinum's `useVerifyAuthentication`.
 * @returns {{isEnforced: boolean, isChecking: boolean, isRedirecting: boolean}}
 *   `isEnforced` is false on non-Alta hosts, where the caller should render
 *   normally. `isChecking` is true while Helium has not answered yet.
 *   `isRedirecting` is true once a navigation to sign-in has been triggered.
 */
export const useTokenGuard = ({ isRedirectUrlEnabled = true } = {}) => {
  const opEnv = getOpEnv();
  const isEnforced = Boolean(opEnv);

  const [isChecking, setIsChecking] = useState(isEnforced);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Guards against a second navigation. React StrictMode runs effects twice in
  // development, and assigning `location.href` again mid-navigation would
  // restart it.
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!isEnforced) return undefined;

    const abortController = new AbortController();

    const redirectToSignIn = () => {
      if (hasRedirected.current) return;
      hasRedirected.current = true;

      const signInUrl = getSignInUrl(
        opEnv,
        isRedirectUrlEnabled ? window.location.href : undefined,
      );

      // Unreachable for a known env, but bailing out beats navigating to
      // "undefined" if the env table and the guard ever disagree.
      if (!signInUrl) {
        setIsChecking(false);
        return;
      }

      setIsRedirecting(true);

      // A full page load, not a router navigation: sign-in is a different app
      // on a different origin.
      window.location.assign(signInUrl);
    };

    const token = getAccessToken(opEnv);

    // No token at all — nothing to validate, so skip the round trip.
    if (!token) {
      redirectToSignIn();
      return () => abortController.abort();
    }

    (async () => {
      const { status, error } = await validateAccessToken(
        token,
        opEnv,
        abortController.signal,
      );

      if (abortController.signal.aborted) return;

      if (status === TOKEN_STATUS.INVALID) {
        removeAccessToken(opEnv);
        redirectToSignIn();
        return;
      }

      if (status === TOKEN_STATUS.UNKNOWN) {
        // Surfaced rather than swallowed: this is the branch that lets an
        // unauthenticated user through, so it needs to be visible when
        // diagnosing why the guard did not fire.
        console.warn(
          '[useTokenGuard] Could not verify the access token; rendering anyway.',
          error,
        );
      }

      setIsChecking(false);
    })();

    return () => abortController.abort();
    // opEnv and isRedirectUrlEnabled are derived from the hostname and a
    // caller constant, so neither changes across the lifetime of the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isEnforced, isChecking, isRedirecting };
};

export default useTokenGuard;
